import { memo, useCallback, useEffect, useMemo, useState, useSyncExternalStore } from 'react';
import { Transition } from '@headlessui/react';
import { TranscriptExtension, TranscriptSelection } from '../types';
import { getEditTranscriptDuration, getTimeWithoutDeletesForRange } from '../utils';
import {
  CLIP_MIN_DURATION,
  CLIP_MIN_DURATION_ERROR,
  HOOK_MIN_DURATION_IN_SECS,
  TRANSCRIPT_MAX_WORD_EDIT_LENGTH
} from '../constants';
import TranscriptHideMenuOptions from './TranscriptHideMenuOptions';
import { getSelectionBoundaryTrimUpdate } from './utils';
import IconButton from '@/components/atoms/Button/IconButton';
import { isSplitsFeatureEnabled, isSplitsInTimelineEnabled } from '@/stores/clip';
import { useClipsContext } from '@/context/ClipsContext/ClipsContext';
import TooltipV2 from '@/components/TooltipV2';
import {
  getAllSegments,
  getClipFPS,
  isClipInOriginalLanguage,
  isFullLengthClip
} from '@/player-config/src/utils/clipUtils';
import useTranscriptPage from '@/Pages/TranscriptPage/useTranscriptPage';
import { updateClipData } from '@/Pages/Clip/ClipPlayer/ClipNewTimeline/clipUpdateDataUtils';
import { CLIP_UPDATE_TYPE } from '@/Pages/Clip/ClipPlayer/ClipNewTimeline/clipUpdateDataUtils/constants';
import useCreateHookSegment from '@/Pages/Clip/ClipPlayer/ClipNewTimeline/hooks/useCreateHookSegment';
import { FeatureFlagKeys } from '@/services/featureFlag';
import featureFlagStore from '@/stores/featureFlagStore';
import useCreateClip from '@/Pages/Clip/ClipPlayer/hooks/useCreateClip';
import { useRemotionPlayerSeek } from '@/App/remotion/MainPlayer/ClipPlayerV2WithNewTimeline/hooks/useRemotionPlayerSeek';
import {
  getAbsoluteTimeFromSegmentTime,
  getPlayerTimeFromSegmentTime
} from '@/Pages/Clip/ClipPlayer/ClipNewTimeline/timeUtils';
import { getClipTotalDurationInFrames } from '@/App/remotion/MainPlayer/utils/configUtils';
import { SegmentType } from '@/player-config/src/types/Segment/Type';
import { approxEquals } from '@/player-config/src/utils/compareUtils';
import { CompositeSegment } from '@/player-config/src/types/RemotionPlayerConfigTypes/Segment/CompositeSegment';

const RESTORE_SELECTION_DURATION_TOLERANCE = 1e-3;

const DeleteButton = ({
  isDeleteDisabled,
  clipCaptionsLoading,
  deleteSelectedRanges
}: {
  isDeleteDisabled: boolean;
  clipCaptionsLoading: boolean;
  deleteSelectedRanges: () => void;
}) => {
  return (
    <TooltipV2
      place="left"
      tooltipId="delete-tooltip"
      tooltipContent={isDeleteDisabled ? CLIP_MIN_DURATION_ERROR : 'Delete'}
      size="xsmall"
      tooltipElement={
        <IconButton
          icon={clipCaptionsLoading ? 'IconLoader2' : 'IconTrash'}
          trackingId="icon-delete-text"
          onClick={deleteSelectedRanges}
          variation="checkbox"
          disabled={isDeleteDisabled || clipCaptionsLoading}
          iconClassName={clipCaptionsLoading ? 'animate-spin' : ''}
          buttonClassName="w-10 h-10"
          data-tooltip-id="delete-tooltip"
        />
      }
    />
  );
};

const RestoreButton = ({
  clipCaptionsLoading,
  restoreDeletedWords
}: {
  clipCaptionsLoading: boolean;
  restoreDeletedWords: () => void;
}) => {
  return (
    <TooltipV2
      place="left"
      tooltipId="restore-tooltip"
      tooltipContent="Restore"
      size="xsmall"
      tooltipElement={
        <IconButton
          icon={clipCaptionsLoading ? 'IconLoader2' : 'IconRestore'}
          trackingId="icon-delete-text"
          onClick={restoreDeletedWords}
          variation="checkbox"
          disabled={clipCaptionsLoading}
          iconClassName={clipCaptionsLoading ? 'animate-spin' : ''}
          buttonClassName="w-10 h-10"
          data-tooltip-id="restore-tooltip"
        />
      }
    />
  );
};

function EditTranscriptMenu({
  show,
  editPopupRef,
  isCreatingClip,
  onClose,
  onCreate,
  onStartEditing,
  onEditActionTriggered,
  selectionStartTime,
  selectionEndTime,
  transcriptSelection,
  selectionStartSegmentId,
  selectionEndSegmentId,
  transcriptExtension
}: {
  show: boolean;
  editPopupRef: React.MutableRefObject<HTMLDivElement | null>;
  isCreatingClip?: boolean;
  transcriptSelection?: TranscriptSelection | null;
  onClose: () => void;
  onCreate: (start: number, end: number, startSegmentId?: string, endSegmentId?: string) => void;
  onStartEditing: () => void;
  onEditActionTriggered: () => void;
  selectionStartTime: number;
  selectionEndTime: number;
  selectionStartSegmentId: string;
  selectionEndSegmentId: string;
  transcriptExtension?: TranscriptExtension;
}) {
  const { clipId, clipData, clipCaptionsLoading } = useClipsContext();
  const { seekTo } = useRemotionPlayerSeek();
  const createHook = useCreateHookSegment();
  const { isCreateClipAllowed } = useCreateClip({ clip: clipData });
  const { createNewChapter } = useTranscriptPage();
  const [isDeletingWords, setIsDeletingWords] = useState<boolean | undefined>(undefined);
  const [isNewChapterLoading, setIsNewChapterLoading] = useState<boolean>(false);

  const featureFlags = useSyncExternalStore(featureFlagStore.subscribe, featureFlagStore.getSnapshot);
  const shouldEnableMultiWordEditWithDifferentSrtIndex =
    featureFlags[FeatureFlagKeys.Use_CL_Multi_Word_Edit_Transcript_No_Srt_Check];
  const isSplitsFeatureEnable = isSplitsFeatureEnabled(featureFlags, clipData);
  const isSplitsEnabled = isSplitsInTimelineEnabled(clipData);

  const onPlay = useCallback(
    (time: number, play?: boolean) => {
      seekTo(time, play);
    },
    [seekTo]
  );

  useEffect(() => {
    if (isDeletingWords && !clipCaptionsLoading) {
      onClose();
      setIsDeletingWords(false);
    }
  }, [isDeletingWords, clipCaptionsLoading, onClose]);

  const totalTimeWithoutDeletes = useMemo(() => {
    return getTimeWithoutDeletesForRange({
      startTime: selectionStartTime,
      endTime: selectionEndTime,
      startSegmentId: selectionStartSegmentId,
      endSegmentId: selectionEndSegmentId,
      clipData
    });
  }, [selectionStartTime, selectionEndTime, selectionStartSegmentId, selectionEndSegmentId, clipData]);

  const isClipTranslated = useMemo(() => {
    return !isClipInOriginalLanguage(clipData);
  }, [clipData]);

  const canShowDelete = useMemo(() => {
    return !isClipTranslated;
  }, [isClipTranslated]);

  const isDeleteOnlySelection = totalTimeWithoutDeletes === 0;

  const { shouldShowRestoreButton, trimUpdate } = useMemo(() => {
    if (
      selectionStartTime !== null &&
      selectionEndTime !== null &&
      selectionStartSegmentId !== null &&
      selectionEndSegmentId !== null
    ) {
      const allSegments = getAllSegments(clipData);
      const startSegment = allSegments.find(segment => segment.id === selectionStartSegmentId);
      if (!startSegment) {
        return { shouldShowRestoreButton: false, trimUpdate: null };
      }
      const endSegment = allSegments.find(segment => segment.id === selectionEndSegmentId);
      if (!endSegment) {
        return { shouldShowRestoreButton: false, trimUpdate: null };
      }
      if (startSegment.type !== SegmentType.COMPOSITE || endSegment.type !== SegmentType.COMPOSITE) {
        return { shouldShowRestoreButton: false, trimUpdate: null };
      }
      if (selectionStartTime === selectionEndTime && selectionStartSegmentId === selectionEndSegmentId) {
        return { shouldShowRestoreButton: false, trimUpdate: null };
      }
      const playerStartTime = getPlayerTimeFromSegmentTime(selectionStartTime, clipData, selectionStartSegmentId);
      const absoluteStartTime = getAbsoluteTimeFromSegmentTime(
        selectionStartTime,
        clipData,
        selectionStartSegmentId,
        -Infinity
      );
      const playerEndTime = getPlayerTimeFromSegmentTime(selectionEndTime, clipData, selectionEndSegmentId);
      const absoluteEndTime = getAbsoluteTimeFromSegmentTime(
        selectionEndTime,
        clipData,
        selectionEndSegmentId,
        -Infinity
      );

      // for splits, also check if there are any boundaries outside of selection
      // if so, we should show restore button and trim selection to those boundaries on restore
      if (isSplitsEnabled) {
        const selectedSegmentsIds = Object.keys(transcriptSelection?.words || {});
        const selectedSegments = allSegments.filter((segment): segment is CompositeSegment => {
          return selectedSegmentsIds.includes(segment.id) && segment.type === SegmentType.COMPOSITE;
        });
        for (const segment of selectedSegments) {
          const outsideBoundaries = getSelectionBoundaryTrimUpdate(segment, transcriptSelection, transcriptExtension);
          // this is returning because we can have only 1 segment extended. If we change
          // that logic in the future, we might need to handle multiple segments with extended boundaries.
          if (outsideBoundaries) {
            return { shouldShowRestoreButton: true, trimUpdate: outsideBoundaries };
          }
        }
      }

      return {
        shouldShowRestoreButton: !approxEquals(
          playerEndTime - playerStartTime,
          isSplitsEnabled ? absoluteEndTime - absoluteStartTime : selectionEndTime - selectionStartTime,
          RESTORE_SELECTION_DURATION_TOLERANCE
        ),
        trimUpdate: null
      };
    }
    return { shouldShowRestoreButton: false, trimUpdate: null };
  }, [
    selectionStartTime,
    selectionEndTime,
    selectionStartSegmentId,
    selectionEndSegmentId,
    clipData,
    isSplitsEnabled,
    transcriptSelection,
    transcriptExtension
  ]);

  const isCreateClipDisabled = useMemo(() => {
    return totalTimeWithoutDeletes < CLIP_MIN_DURATION;
  }, [totalTimeWithoutDeletes]);

  const formattedDuration = useMemo(
    () =>
      totalTimeWithoutDeletes === 0
        ? getEditTranscriptDuration(selectionEndTime - selectionStartTime)
        : getEditTranscriptDuration(totalTimeWithoutDeletes),
    [selectionEndTime, selectionStartTime, totalTimeWithoutDeletes]
  );

  const isDeleteDisabled = useMemo(() => {
    return getClipTotalDurationInFrames(clipData) / getClipFPS(clipData) - totalTimeWithoutDeletes < 5;
  }, [clipData, totalTimeWithoutDeletes]);

  const deleteSelectedRanges = useCallback(() => {
    onEditActionTriggered();

    const newClipData = updateClipData({
      clipId,
      actionType: CLIP_UPDATE_TYPE.ADD_COMPOSITE_SEGMENT_DELETE,
      startTime: selectionStartTime,
      startSegmentId: selectionStartSegmentId,
      endTime: selectionEndTime,
      endSegmentId: selectionEndSegmentId,
      deleteType: 'MANUAL'
    });
    const newPlayerTime = getPlayerTimeFromSegmentTime(selectionStartTime, newClipData, selectionStartSegmentId);

    onPlay?.(newPlayerTime);

    onClose();
    setIsDeletingWords(true);
  }, [
    onEditActionTriggered,
    selectionStartTime,
    selectionEndTime,
    clipId,
    selectionStartSegmentId,
    selectionEndSegmentId,
    onPlay,
    onClose
  ]);

  const playFromTime = useCallback(() => {
    if (clipId) {
      const newPlayerTime = getPlayerTimeFromSegmentTime(selectionStartTime, clipData, selectionStartSegmentId);
      onPlay?.(newPlayerTime, true);
      onClose();
    }
  }, [clipData, clipId, onClose, onPlay, selectionStartSegmentId, selectionStartTime]);

  // Should only work when same segment is selected
  const restoreDeletedWords = useCallback(() => {
    if (trimUpdate) {
      const updateParams: any = {
        clipId,
        actionType: CLIP_UPDATE_TYPE.UPDATE_SEGMENT_TRACK,
        compositeId: trimUpdate.segmentId,
        trackId: trimUpdate.trackId,
        type: SegmentType.COMPOSITE
      };

      if (trimUpdate.startTime !== undefined) {
        updateParams.startTime = trimUpdate.startTime;
      }
      if (trimUpdate.endTime !== undefined) {
        updateParams.endTime = trimUpdate.endTime;
      }

      updateClipData(updateParams, { skipBackendUpdate: true });
    }
    let finalClipData = updateClipData({
      clipId,
      actionType: CLIP_UPDATE_TYPE.RESTORE_COMPOSITE_SEGMENT_DELETE,
      startTime: selectionStartTime,
      endTime: selectionEndTime,
      startCompositeId: selectionStartSegmentId,
      endCompositeId: selectionEndSegmentId
    });

    // Re-delete the gap between the old segment boundary and the selection start/end.
    // When restoring via a boundary extension, the whole region from the old boundary to
    // the new segment end gets restored, but the user only selected a sub-range of that.
    if (trimUpdate?.originalEndTime !== undefined && selectionStartTime > trimUpdate.originalEndTime) {
      finalClipData = updateClipData({
        clipId,
        actionType: CLIP_UPDATE_TYPE.ADD_COMPOSITE_SEGMENT_DELETE,
        startTime: trimUpdate.originalEndTime,
        startSegmentId: trimUpdate.segmentId,
        endTime: selectionStartTime,
        endSegmentId: selectionStartSegmentId,
        deleteType: 'MANUAL'
      });
    } else if (trimUpdate?.originalStartTime !== undefined && selectionEndTime < trimUpdate.originalStartTime) {
      finalClipData = updateClipData({
        clipId,
        actionType: CLIP_UPDATE_TYPE.ADD_COMPOSITE_SEGMENT_DELETE,
        startTime: selectionEndTime,
        startSegmentId: selectionEndSegmentId,
        endTime: trimUpdate.originalStartTime,
        endSegmentId: trimUpdate.segmentId,
        deleteType: 'MANUAL'
      });
    }

    const newPlayerTime = getPlayerTimeFromSegmentTime(selectionStartTime, finalClipData, selectionStartSegmentId);

    onClose();
    onEditActionTriggered();
    onPlay?.(newPlayerTime);
    setIsDeletingWords(true);
  }, [
    clipId,
    onClose,
    onEditActionTriggered,
    onPlay,
    selectionEndSegmentId,
    selectionEndTime,
    selectionStartSegmentId,
    selectionStartTime,
    trimUpdate
  ]);

  const isChapterCreationAllowed = useMemo(() => {
    return isFullLengthClip(clipData);
  }, [clipData]);

  const createClip = useCallback(() => {
    onCreate(selectionStartTime, selectionEndTime, selectionStartSegmentId, selectionEndSegmentId);
  }, [onCreate, selectionStartTime, selectionEndTime, selectionStartSegmentId, selectionEndSegmentId]);

  const onNewChapter = useCallback(async () => {
    setIsNewChapterLoading(true);
    try {
      const finalTime = isSplitsEnabled
        ? getPlayerTimeFromSegmentTime(selectionStartTime, clipData, selectionStartSegmentId)
        : selectionStartTime;
      await createNewChapter('New Chapter', finalTime);
    } finally {
      setIsNewChapterLoading(false);
    }
  }, [clipData, createNewChapter, isSplitsEnabled, selectionStartSegmentId, selectionStartTime]);

  const canShowRestore = useMemo(() => {
    return !isClipTranslated;
  }, [isClipTranslated]);

  const isAddHookVisible = useMemo(() => {
    if (isClipTranslated) {
      return false;
    }
    const duration = selectionEndTime - selectionStartTime;
    return (
      duration >= HOOK_MIN_DURATION_IN_SECS &&
      isSplitsFeatureEnable &&
      selectionStartSegmentId === selectionEndSegmentId
    );
  }, [
    isSplitsFeatureEnable,
    selectionEndSegmentId,
    selectionEndTime,
    selectionStartSegmentId,
    selectionStartTime,
    isClipTranslated
  ]);

  const handleCreateHookSegment = useCallback(() => {
    createHook({
      startTime: selectionStartTime,
      endTime: selectionEndTime,
      startSegmentId: selectionStartSegmentId,
      endSegmentId: selectionEndSegmentId,
      clipData,
      clipId
    });

    onEditActionTriggered();
    onClose();
  }, [
    clipData,
    clipId,
    selectionStartTime,
    selectionEndTime,
    selectionStartSegmentId,
    selectionEndSegmentId,
    onEditActionTriggered,
    onClose,
    createHook
  ]);

  return (
    <Transition
      appear
      show={show}
      className="absolute -right-10 top-0 z-20 flex h-full w-12 items-center"
      enter="transition duration-600 ease-out"
      enterFrom="transform translate-x-[10px] opacity-0"
      enterTo="transform translate-x-0 opacity-100"
      leave="transition duration-100 ease-out"
      leaveFrom="transform translate-x-0 opacity-100"
      leaveTo="transform translate-x-[10px] opacity-0"
    >
      <div className="absolute z-30">
        <div className="top-0 flex h-full w-12 shrink-0 items-center justify-center">
          <div
            className="flex flex-col items-center justify-center rounded-lg border bg-white p-1.5 shadow"
            ref={editPopupRef}
          >
            <div className="mb-1 w-max rounded-lg bg-black p-1 text-center text-xs text-white">{formattedDuration}</div>
            {isDeleteOnlySelection && selectionStartSegmentId === selectionEndSegmentId ? (
              canShowRestore ? (
                <RestoreButton clipCaptionsLoading={clipCaptionsLoading} restoreDeletedWords={restoreDeletedWords} />
              ) : (
                <></>
              )
            ) : (
              <>
                <TooltipV2
                  place="left"
                  tooltipId="play-video-tooltip"
                  tooltipContent="Play video"
                  size="xsmall"
                  tooltipElement={
                    <IconButton
                      icon="IconPlayerPlay"
                      trackingId="icon-play-full-recording"
                      onClick={playFromTime}
                      variation="checkbox"
                      buttonClassName="w-10 h-10"
                      data-tooltip-id="play-video-tooltip"
                    />
                  }
                />
                {isCreateClipAllowed ? (
                  <TooltipV2
                    place="left"
                    tooltipId="create-clip-tooltip"
                    tooltipContent={isCreateClipDisabled ? CLIP_MIN_DURATION_ERROR : 'Create clip'}
                    size="xsmall"
                    tooltipElement={
                      <IconButton
                        icon={isCreatingClip ? 'IconLoader2' : 'IconMovie'}
                        trackingId="icon-create-clip"
                        onClick={createClip}
                        disabled={isCreateClipDisabled || isCreatingClip}
                        variation="checkbox"
                        iconClassName={isCreatingClip ? 'animate-spin' : ''}
                        buttonClassName="w-10 h-10"
                        data-tooltip-id="create-clip-tooltip"
                      />
                    }
                  />
                ) : (
                  <></>
                )}
                {(shouldEnableMultiWordEditWithDifferentSrtIndex || transcriptSelection?.isSingleParagraphSelection) &&
                  transcriptSelection &&
                  selectionStartSegmentId === selectionEndSegmentId &&
                  transcriptSelection.words[selectionStartSegmentId]?.length <= TRANSCRIPT_MAX_WORD_EDIT_LENGTH && (
                    <TooltipV2
                      place="left"
                      tooltipId="correct-transcript-tooltip"
                      tooltipContent="Correct"
                      size="xsmall"
                      tooltipElement={
                        <IconButton
                          icon="IconEdit"
                          trackingId="icon-correct-transcript"
                          onClick={onStartEditing}
                          variation="checkbox"
                          disabled={clipCaptionsLoading}
                          buttonClassName="w-10 h-10"
                          data-tooltip-id="correct-transcript-tooltip"
                        />
                      }
                    />
                  )}

                {isChapterCreationAllowed ? (
                  <TooltipV2
                    place="left"
                    tooltipId="new-chapter-tooltip"
                    tooltipContent="New Chapter"
                    size="xsmall"
                    tooltipElement={
                      <IconButton
                        icon="IconList"
                        trackingId="icon-new-chapter"
                        onClick={onNewChapter}
                        variation="checkbox"
                        disabled={isNewChapterLoading}
                        buttonClassName="w-10 h-10"
                        data-tooltip-id="new-chapter-tooltip"
                      />
                    }
                  />
                ) : (
                  <></>
                )}

                {
                  <TranscriptHideMenuOptions
                    transcriptSelection={transcriptSelection}
                    onCloseMenu={onClose}
                    onEditActionTriggered={onEditActionTriggered}
                  />
                }
                {canShowDelete && (
                  <DeleteButton
                    isDeleteDisabled={isDeleteDisabled}
                    clipCaptionsLoading={clipCaptionsLoading}
                    deleteSelectedRanges={deleteSelectedRanges}
                  />
                )}
                {shouldShowRestoreButton && canShowRestore && (
                  <RestoreButton clipCaptionsLoading={clipCaptionsLoading} restoreDeletedWords={restoreDeletedWords} />
                )}
                {isAddHookVisible && (
                  <TooltipV2
                    place="left"
                    tooltipId="add-as-hook-tooltip"
                    tooltipContent="Add as hook"
                    size="xsmall"
                    tooltipElement={
                      <IconButton
                        icon="IconArrowBarToLeft"
                        trackingId="icon-add-as-hook"
                        onClick={handleCreateHookSegment}
                        variation="checkbox"
                        buttonClassName="w-10 h-10"
                        data-tooltip-id="add-as-hook-tooltip"
                      />
                    }
                  />
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </Transition>
  );
}

export default memo(EditTranscriptMenu);
